// fetchData.ts
import axios from 'axios'
import * as cheerio from 'cheerio';
import { parse as json2csvParse } from 'json2csv';
import { parse as csvParse } from 'csv-parse/sync';
import { NFLPlayer, MLBPlayer , NFLPosition, MLBPosition, Player, NFLTeams, PositionDataMLB, NBAPlayer, PositionDataNBA, NBAPosition, NBATeams, MLBTeams} from '../data-components/Player';
import { calculatePositionRanks } from '../data-components/startupData';
import { NBATeamAbbreviations, NFLTeamAbbreviations, MLBTeamAbbreviations, MLBPositionAbbreviationMap } from '../data-components/typeGuards';
import { Sport } from '../App';
import { parse } from 'path';
import { url } from 'inspector';

// const currentYear = (new Date()).getFullYear();

interface RookieData {
  name: string;
  team: string;
  position: string;
}

interface ByeWeek {
  team: string;
  byeWeek: number;
}

interface NFLPlayerJSON {
  player_id: number;
  player_name: string;
  sportsdata_id: string;
  player_team_id: string;
  player_position_id: string;
  player_positions: string;
  player_short_name: string;
  player_eligibility: string;
  player_yahoo_positions: string;
  player_page_url: string;
  player_filename: string;
  player_square_image_url: string;
  player_image_url: string;
  player_yahoo_id: string;
  cbs_player_id: string;
  player_bye_week: string;
  player_owned_avg: number;
  player_owned_espn: number;
  player_owned_yahoo: number;
  player_ecr_delta: number | null;
  rank_ecr: number;
  rank_min: string;
  rank_max: string;
  rank_ave: string;
  rank_std: string;
  pos_rank: string;
  tier: number;
}

interface MLBPlayerJSON {
  player_id: number;
  player_name: string;
  player_short_name: string;
  player_team_id: string;
  filename: string;
  position_id: string;
  primary_position: string;
  player_positions: string;
  yahoo_player_id: string;
  player_yahoo_id: string;
  cbs_player_id: string;
  has_image: string;
  player_yahoo_positions: string;
  player_espn_positions: string;
  player_cbs_positions: string;
  player_page_url: string;
  player_square_image_url: string;
  player_image_url: string;
  rank_ecr: number;
  rank_min: string;
  rank_max: string;
  rank_ave: string;
  rank_std: string;
  pos_rank: string;
}

interface NBAPlayerJSON {
  player_id: number;
  player_name: string;
  player_short_name: string;
  player_team_id: string;
  player_position_id: string;
  player_positions: string;
  player_page_url: string;
  filename: string;
  player_square_image_url: string;
  player_image_url: string;
  player_yahoo_id: string;
  player_nbacom_id: string;
  player_cbs_id: string;
  rank_ecr: number;
  rank_min: string;
  rank_max: string;
  rank_ave: string;
  rank_std: string;
  pos_rank: string;
}

const localHost = window.location.hostname === 'localhost'
const useProxy = true;
// const proxyURL = localHost ? `http://localhost:3000/api/proxy` : `https://draftdeck-proxy.vercel.app/api/proxy`;
const proxyURL = `https://tesseract-api.com/v1/general/proxy`;
// const proxyURL = `https://u5m1rc2bg3.execute-api.us-east-1.amazonaws.com/dev/general/proxy`;

//these are what need to be in the apiConfigs file
enum TesseractAPI {
  FantasyPros = "FantasyPros",
  Wikipedia = "Wikipedia",
  SportsDataIO = "SportsDataIO"
}

export const fetchNFLData = async (year: number): Promise<Player[]> => {
  try {

    const URL = `https://partners.fantasypros.com/api/v1/consensus-rankings.php?sport=NFL&year=${year}&week=0&id=1663&position=ALL&type=DRAFT&scoring=PPR`;

    if (localHost)
    {
      console.log("nfl url:", URL);
    }

    let response = await axiosProxy(URL, TesseractAPI.FantasyPros);

    // if (response.data.players)
    // {
    //   const prevYearURL = `https://partners.fantasypros.com/api/v1/consensus-rankings.php?sport=NFL&year=${currentYear - 1}&week=0&id=1663&position=ALL&type=ST&scoring=PPR`;
    //   response = await axiosProxy(prevYearURL, TesseractAPI.FantasyPros);
    // }

    //const csvData = response.data;
    const playerListJSON = response.data.players;
    // const playerListJSON = JSON.parse(response.data.players);

    const nflPlayers: NFLPlayer[] = playerListJSON.map((json: NFLPlayerJSON) =>
    {

        const newPlayer = new NFLPlayer
        (
          json.player_name,
          json.player_team_id,
          json.player_position_id as NFLPosition,
          json.rank_ecr,
          parseFloat(json.rank_ave),
          parsePositionRank(json.pos_rank),
          parseInt(json.player_bye_week),
          false, false, false
        );

        return newPlayer;
    });

    const calculatedPlayers = await addDerivedDataNFL(nflPlayers, false, year);
    if (localHost)
    {
      console.log("fetched nfl");
    }
    return calculatedPlayers;
  }
  catch (error) {
    console.error('Error fetching NFL data:', error);
    throw error;
  }
};

const cleanCSVData = (csvData: string): string => {
  // Remove trailing commas from each line
  return csvData.split('\n').map(line => line.replace(/,\s*$/, '')).join('\n');
};

function parsePositionRank(rankString: string): number | null {
  const match = rankString.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
}


const sanitizeName= (name: string): string => {
  return name.replace(/[^a-zA-Z]/g, '');
}

const NFLPlayerMatch = (nflPlayer: NFLPlayer, rookie: RookieData) : boolean => 
{
  const nameMatch = sanitizeName(nflPlayer.name.trim().toLocaleLowerCase()) === sanitizeName(rookie.name.trim().toLocaleLowerCase());
//  const positionMatch = nflPlayer.position === rookie.position;
  const positionMatch = rookie.position.split("/").includes(nflPlayer.position);

  // const teamMatch = nflPlayer.team === rookie.team;

  return nameMatch && positionMatch ;//&& teamMatch;
}

const NBAPlayerMatch = (nbaPlayer: NBAPlayer, rookie: RookieData) : boolean => 
  {
    const nameMatch = sanitizeName(nbaPlayer.name.trim().toLocaleLowerCase()) === sanitizeName(rookie.name.trim().toLocaleLowerCase());
    // const positionMatch = nbaPlayer.position === rookie.position;
    const rookieTeamDerived = getNBATeamAbbreviations(rookie.team);
    const teamMatch = nbaPlayer.team === rookieTeamDerived;
  
    return nameMatch && true && teamMatch;
}

const MLBPlayerMatch = (mlbPlayer: MLBPlayer, rookie: RookieData) : boolean => 
  {
    const nameMatch = sanitizeName(mlbPlayer.name.trim().toLocaleLowerCase()) === sanitizeName(rookie.name.trim().toLocaleLowerCase());
    // const positionMatch = nbaPlayer.position === rookie.position;
     const rookieTeamDerived = getMLBTeamAbbreviations(rookie.team);
     const teamMatch = mlbPlayer.team === rookieTeamDerived;
  
    return nameMatch && true && teamMatch;
}

export const fetchMLBData = async (year: number): Promise<Player[]> => {
  try {
    const URL = `https://partners.fantasypros.com/api/v1/consensus-rankings.php?sport=MLB&year=${year}&week=0&id=1663&position=ALL&type=DRAFT`;
    let response = await axiosProxy(URL, TesseractAPI.FantasyPros);

    if (localHost)
    {
      console.log(`mlb url:`, URL);
    }

    // if (response.data.players)
    // {
    //   const prevYearURL = `https://partners.fantasypros.com/api/v1/consensus-rankings.php?sport=MLB&year=${year - 1}&week=0&id=1663&position=ALL&type=ST`;
    //   response = await axiosProxy(prevYearURL, TesseractAPI.FantasyPros);
    // }

    const playerListJSONALL: MLBPlayerJSON[] = response.data.players;
    const playerListJSONSliced = playerListJSONALL.slice(0,1000);
    
    const rookieIDs = new Set(
      [...playerListJSONSliced]
        .sort((a, b) => b.player_id - a.player_id)  // descending
        .slice(0, 50)
        .map(p => p.player_id)
    );

    // const playerListJSON500 = sorted.slice(0, 1000);
    // const playerListJSON = JSON.parse(response.data.players);
    let ii = 0;
    const mlbPlayers: MLBPlayer[] = playerListJSONSliced.map((json: MLBPlayerJSON) =>
    {
        const mlbPositionData_DERIVED = extrapolatePositionsMLB(
          json.player_positions, 
          json.primary_position, json);

        const isRookie = rookieIDs.has(json.player_id)

        const newPlayer = new MLBPlayer
        (
          json.player_name, 
          json.player_team_id,
          mlbPositionData_DERIVED.mainPosition,
          json.rank_ecr,
          parseFloat(json.rank_ave),
          undefined,
          isRookie, false, false, mlbPositionData_DERIVED.otherPositions
        );
        ii++;
        return newPlayer;
    });

    // const csvData = response.data;
    // const cleanedCSVData = cleanCSVData(csvData);

    // const jsonData = csvParse(cleanedCSVData.split('\n').slice(4).join('\n'), {
    //   columns: true,
    //   skip_empty_lines: true
    // });

    // const mlbPlayers: MLBPlayer[] = jsonData.map((row: any) => {
    // const numericalFields = Object.keys(row)
    //   .filter(key => !['Player Name', 'Team', 'Position', 'Rank'].includes(key) && typeof parseInt(row[key]) === 'number');

    // const adp = numericalFields.reduce((acc, key) => acc + parseFloat(row[key]), 0) / numericalFields.length;

    // const extrapolatedMLB = extrapolatePositionsMLB(row['Position'] as string);

    // const newPlayer = new MLBPlayer(
    //     row['Player Name'],
    //     row['Team'],
    //     extrapolatedMLB.mainPosition,
    //     row['Rank'],
    //     adp, undefined, false, false, false, extrapolatedMLB.otherPositions );

    //     return newPlayer;
    // });

    const calculatedPlayers = calculatePositionRanks(mlbPlayers);

    const derivedPlayers = calculatedPlayers;//;await addDerivedDataMLB(calculatedPlayers as MLBPlayer[], year);

    if (localHost)
    {
      console.log("fetched mlb")
    }
    return derivedPlayers;

  } catch (error) {
    console.error('Error fetching MLB data:', error);
    throw error;
  }
};

export const fetchNBAData = async (year: number): Promise<Player[]> => {
  try {
    const URL = `https://partners.fantasypros.com/api/v1/consensus-rankings.php?sport=NBA&year=${year}&week=0&position=ALL&type=DRAFT&scoring=PTS`;

    // console.log(URL);

    if (localHost)
    {
      console.log(`nba url (${year}):`, URL);
    }

    let response = await axiosProxy(URL, TesseractAPI.FantasyPros);
    // console.log("response players:", response.data.players);

    if (!response.data.players || response.data.players.length < 2) //sometimes 1 was returning
    {
      const prevYearURL = `https://partners.fantasypros.com/api/v1/consensus-rankings.php?sport=NBA&year=${year - 1}&week=0&position=ALL&type=DRAFT&scoring=PTS`;

      if (localHost)
      {
        console.log(`blank player data, nba url (${year - 1}):`, URL);
      }

      response = await axiosProxy(prevYearURL, TesseractAPI.FantasyPros);
    }

    const playerListJSONALL: NBAPlayerJSON[] = response.data.players;
    const playerListJSON500 = playerListJSONALL.slice(0,500);
    // const playerListJSON = JSON.parse(response.data.players);
    let ii = 0;
    const nbaPlayers: NBAPlayer[] = playerListJSON500.map((json: NBAPlayerJSON) =>
    {
        const nbaPositionData_DERIVED = extrapolatePositionsNBA(json.player_positions, json);

        const teamDerived = getNBATeamAbbreviations(json.player_team_id);

        const newPlayer = new NBAPlayer
        (
          json.player_name, 
          teamDerived,
          nbaPositionData_DERIVED.mainPosition,
          json.rank_ecr,
          parseFloat(json.rank_ave),
          undefined,
          false, false, false, nbaPositionData_DERIVED.otherPositions
        );
        ii++;
        return newPlayer;
    });

    const calculatedPlayers = calculatePositionRanks(nbaPlayers);

    const derivedPlayers = await addDerivedDataNBA(calculatedPlayers as NBAPlayer[], year);

    if (localHost)
    {
      console.log("fetched nba")
    }
    return derivedPlayers;

  } catch (error) {
    console.error('Error fetching NBA data:', error);
    throw error;
  }
};

const derivedPositionMLB = (positionStringRaw: string, primaryPositionRaw: string, player: any = undefined): MLBPosition => {
  const infield = ["1B", "2B", "3B", "SS"];
  const outfield = ["LF", "RF", "CF"];
  const primaryForCSVImport = positionStringRaw.includes(",") ? positionStringRaw.split(",")[0] : positionStringRaw
  //only time we dont pass this is in csv import, so just have to take first availble
  let workingPosition = primaryPositionRaw === "" ? primaryForCSVImport : primaryPositionRaw;

  if (workingPosition.includes("P"))
  {
    workingPosition =  (positionStringRaw.includes("SP") || workingPosition === "SP") ? "SP" : "RP";
  }

  if (outfield.includes(workingPosition))
  {
    return MLBPosition.OF;
  }

  if (infield.includes(workingPosition))
  {
    switch (workingPosition)
    {
      case "1B":
        workingPosition = MLBPosition.B1;
        break;
      case "2B":  
        workingPosition =  MLBPosition.B2;
        break;
      case "3B":
        workingPosition =  MLBPosition.B3;
        break;
      case "SS":
        workingPosition = MLBPosition.SS;
        break;
    }
  }

  return workingPosition as MLBPosition;

}

const fetchNFLRookies = async (year: number): Promise<RookieData[]> => {
  
  try {
    const $: cheerio.CheerioAPI = await getRookieDataFromWikipedia(Sport.NFL, year);

    const players: RookieData[] = [];

    $('table.wikitable.sortable.plainrowheaders tbody tr').each((index, element) => {
      const teamName = $(element).find('td').eq(1).text().trim();
      const name = $(element).find('td').eq(2).text().trim().replace(/[^\p{L}\p{N}]+$/u, '');;
      const position = $(element).find('td').eq(3).text().trim();

      const teamDerived = getNFLTeamAbbreviations(teamName);

      if (name) {
        players.push({ name, team: teamDerived, position });

      }
    });

    // console.log(players);

    if (localHost)
    {
      console.log("fetched nfl rookies")
    }
    return players;
  } catch (error) {
    console.error('Error fetching NFL rookies from Wikipedia:', error);
    return [];
  }
};

const fetchNBARookies = async (year: number): Promise<RookieData[]> => {


  try {
    const $: cheerio.CheerioAPI = await getRookieDataFromWikipedia(Sport.NBA, year);

    const players: RookieData[] = [];

    // console.log(htmlFinal);

    $('table.wikitable.sortable.plainrowheaders tbody tr').each((index, element) => {
      const teamName = $(element).find('td').eq(5).text().trim();
      const name = $(element).find('td').eq(2).text().trim().replaceAll("#", "");
      const position = $(element).find('td').eq(3).text().trim();

      const teamNameCleaned = teamName.replace(/^(.*?)\s?[\(\[].*/, '$1');

      const teamDerived = getNBATeamAbbreviations(teamNameCleaned);

      if (name) {
        players.push({ name, team: teamDerived, position });

      }
    });

    if (localHost)
    {
      console.log("fetched nba rookies")
    }

    return players;
  } catch (error) {
    console.error('Error fetching NBA rookies from Wikipedia:', error);
    return [];
  }
};

const fetchMLBRookies = async (year: number): Promise<RookieData[]> => {

  try {
    const $: cheerio.CheerioAPI = await getRookieDataFromWikipedia(Sport.MLB, year );

    const rookiePlayers: RookieData[] = [];

    const rawRookies = parseDraftTables($);

    rawRookies.forEach(r => {
      r.team = getMLBTeamAbbreviations(r.team);
      r.position = getMLBPositionsConversion(r.position);
      
      if (r.name) {
        rookiePlayers.push(r);
      }
    });

    if (localHost)
    {
      console.log("fetched mlb rookies")
    }

    return rookiePlayers;
  } catch (error) {
    console.error('Error fetching MLB rookies from Wikipedia:', error);
    return [];
  }
};

const parseDraftTables = ($: cheerio.CheerioAPI) => {
  const results: { name: string; team: string; position: string }[] = [];

  $('table').each((_, table) => {
    const headers = $(table).find('tr').first().find('th');
    const headerTexts = headers.map((_, th) => $(th).text().trim().toLowerCase()).get();

    if (!headerTexts.some((text) => text.includes('pick'))) return;

    const playerIndex = headerTexts.findIndex((h) => h.includes('player'));
    const teamIndex = headerTexts.findIndex((h) => h.includes('team'));
    const positionIndex = headerTexts.findIndex((h) => h.includes('position'));

    $(table).find('tr').slice(1).each((_, row) => {
      const cells = $(row).find('td, th'); // include th for player name in first column

      const name = cells.eq(playerIndex).text().trim().replaceAll('#', '');
      const team = cells.eq(teamIndex).text().trim();
      const position = cells.eq(positionIndex).text().trim();

      if (name && team && position) {
        results.push({ name, team, position });
      }
    });
  });

  return results;
};


const getRookieDataFromWikipedia = async (currentSport: Sport, year: number): Promise<cheerio.CheerioAPI> => {

  try {
    let $: cheerio.CheerioAPI;

    let apiSport: string = currentSport;

    switch (currentSport)
    {
      case "MLB":
        apiSport = "Major_League_Baseball";
        break;
    }

    const urlCurrent = `https://en.wikipedia.org/w/api.php?action=parse&page=${year}_${apiSport}_draft&format=json&origin=*`;
    const responseCurrent = await axiosProxy(urlCurrent, TesseractAPI.Wikipedia);
    let htmlCurrentYear = responseCurrent.data.parse.text["*"];

    const $_currentYearData = cheerio.load(htmlCurrentYear);

    //check and pivot to prev year if future draft data
    if (isDraftInFuture($_currentYearData))
    {
      const urlPrev = `https://en.wikipedia.org/w/api.php?action=parse&page=${year-1}_${apiSport}_draft&format=json&origin=*`;
      const prevYearResponse = await axiosProxy(urlPrev, TesseractAPI.Wikipedia);

      //change to prev year if future draft hasnt happened
      const htmlPrevYear = prevYearResponse.data.parse.text["*"];

      $ = cheerio.load(htmlPrevYear);
      // console.log(htmlPrevYear)
    }
    else
    {
      $ = $_currentYearData
      // console.log(htmlCurrentYear)
    }

    return $;
    
  } 
  catch (error) {
    throw error;
  }

} 

const getNFLTeamAbbreviations = (teamNameRaw: string): string => {

  return NFLTeams.includes(teamNameRaw) ? teamNameRaw : (NFLTeamAbbreviations[teamNameRaw] || 'Unknown Team');

}

const getNBATeamAbbreviations = (teamNameRaw: string): string => {

  return NBATeams.includes(teamNameRaw) ? teamNameRaw : (NBATeamAbbreviations[teamNameRaw] || 'Unknown Team');

}

const getMLBTeamAbbreviations = (teamNameRaw: string): string => {

  const teamNameCleaned = teamNameRaw.replace(/\[.*?\]/g, "");

  return MLBTeams.includes(teamNameCleaned) ? teamNameCleaned : (MLBTeamAbbreviations[teamNameCleaned] || 'Unknown Team');
}

const getMLBPositionsConversion = (positionRaw: string): string => {

  const positionCleaned = positionRaw.replace(/\[.*?\]/g, "");

  return MLBPositionAbbreviationMap[positionCleaned];

  // return MLBTeams.includes(teamNameCleaned) ? teamNameCleaned : (MLBTeamAbbreviations[teamNameCleaned] || 'Unknown Team');
}





const apiReplacementString = (apiType: string): string => {
  return `[[${apiType}]]`;
}

const isDraftInFuture = ($: cheerio.CheerioAPI): boolean =>{
  const dateText = $('th.infobox-label')
    .filter((_, el) => $(el).text().trim() === 'Date(s)')
    .next('td')
    .text()
    .trim();

  const dateRangeMatch = dateText.match(/^([A-Za-z]+)\s(\d+)[–-]?/);
  const yearMatch = dateText.match(/(\d{4})$/);

  if (dateRangeMatch && yearMatch) {
    const [_, month, day] = dateRangeMatch;
    const year = yearMatch[1];
    const parsedDate = new Date(`${month} ${day}, ${year}`);
    const today = new Date();

    // Compare just dates, ignore time by zeroing time parts
    parsedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return parsedDate > today;
  }

  return false;
}

export const addDerivedDataNFL = async (nflPlayers: NFLPlayer[], needByeWeekData: boolean = false, year: number): Promise<Player[]> => {

  // const calculatedPlayers = calculatePositionRanks(nflPlayers);

  let byeWeeks: ByeWeek[];

  if (needByeWeekData)
  {
    byeWeeks  = await getByeWeeks(year);
  }

  const NFLRookies = (await fetchNFLRookies(year)).filter(r => 
    r.position.split('/').some(pos => Object.values(NFLPosition).includes(pos as NFLPosition))
  );

  const dataAdded: NFLPlayer[] = nflPlayers.map(p => {
    const isRookie = NFLRookies.some(r => NFLPlayerMatch(p as NFLPlayer, r));
    (p as NFLPlayer).isRookie = isRookie;

    if (needByeWeekData)
    {
      const byeWeek = byeWeeks.find(bw => bw.team === p.team)?.byeWeek;
      (p as NFLPlayer).bye = byeWeek;
    }

    
    return p as NFLPlayer;
  });

  return dataAdded;
  
}

export const addDerivedDataNBA = async (nbaPlayers: NBAPlayer[], year: number): Promise<NBAPlayer[]> => {

  const NBARookies = (await fetchNBARookies(year));
  
  const dataAdded: NBAPlayer[] = nbaPlayers.map(p => {
    const isRookie = NBARookies.some(r => NBAPlayerMatch(p as NBAPlayer, r));
    (p as NBAPlayer).isRookie = isRookie;
    
    return p as NBAPlayer;
  });

  return dataAdded;
  
}

export const addDerivedDataMLB = async (mlbPlayers: MLBPlayer[], year: number): Promise <MLBPlayer[]> => {

    const MLBRookies = (await fetchMLBRookies(year));
  
  const dataAdded: MLBPlayer[] = mlbPlayers.map(p => {
    const isRookie = MLBRookies.some(r => MLBPlayerMatch(p as MLBPlayer, r));
    (p as MLBPlayer).isRookie = isRookie;
    
    return p as MLBPlayer;
  });

  return dataAdded;
  
}

export const extrapolatePositionsMLB = (positionStringRaw: string, primaryPositionRaw: string, player: any): PositionDataMLB => 
{
  const primaryPosition = derivedPositionMLB(positionStringRaw, primaryPositionRaw, player);
  let otherPositions: MLBPosition[] = [];


  if (positionStringRaw.includes(","))
  {
    const rawOtherPositions = (positionStringRaw).split(",").filter(x => x !== primaryPositionRaw);
    const splitOtherPositions = rawOtherPositions.map(x => derivedPositionMLB("", x));
    otherPositions = Array.from(new Set(splitOtherPositions));
  }


  const data: PositionDataMLB = {mainPosition: primaryPosition, otherPositions: otherPositions};

  return data;
}

export const extrapolatePositionsNBA = (positionStringRaw: string, player: any): PositionDataNBA => 
  {
    //had to add this bc fantasypros was just setting basic F or G for rookies
    let positionsFull = positionStringRaw === "F" ? "SF,PF" : positionStringRaw === "G" ? "PG,SG" : positionStringRaw;

    const primaryPosition = positionsFull.split(",")[0] ?? positionsFull; //hasnt tested this
    let otherPositions: NBAPosition[] = [];
  
    if (positionsFull.includes(","))
    {
      // const mainPosition = positionStringRaw.split(",")[0];
      const rawOtherPositions = (positionsFull).split(",").filter(x => x !== primaryPosition) as NBAPosition[];
      // const splitOtherPositions = rawOtherPositions.map(x => derivedPositionMLB("", x));
      otherPositions = Array.from(new Set(rawOtherPositions));
    }
  
    const data: PositionDataNBA = {mainPosition: primaryPosition as NBAPosition, otherPositions: otherPositions};
  
    return data;
  }

const axiosProxy = async (apiURL: string, apiType: TesseractAPI) =>
{
  // const response = useProxy ? await axios.get(proxyURL, { params: { url: apiURL } }) : await axios.get(apiURL);
  const derivedURL = getFullProxyURL(apiURL, apiType);
  const response = useProxy ? await axios.get(derivedURL) : await axios.get(apiURL);
  return response;
}

const getFullProxyURL = (apiURL: string, apiType: TesseractAPI): string => {
  const returnURL = proxyURL + `?TesseractAPI=${apiType}&url=`+ apiURL;
  return returnURL;
}

// Function to fetch NFL bye weeks
const getByeWeeks = async (year: number): Promise<ByeWeek[]> => {
  try {
    const url = `https://api.sportsdata.io/v3/nfl/scores/json/Schedules/${year}?key=${apiReplacementString(TesseractAPI.SportsDataIO)}`;

    // Make the API request
    const { data } = await axiosProxy(url, TesseractAPI.SportsDataIO);

    // Object to store the bye weeks
    const byeWeeks: ByeWeek[] = [];

    // Process the data to extract bye weeks
    data.forEach((game: any) => {
      // Check if the game is a bye week by seeing if there's no opponent (or some similar logic based on API response)
      if (game.AwayTeam === "BYE") {
        const teamNameCleaned = getNFLTeamAbbreviations(game.HomeTeam);
        byeWeeks.push({ team: teamNameCleaned, byeWeek: game.Week}); // Use index + 1 as the week number

      }
    });

    if (localHost)
    {
      console.log("fetched byes")
    }
    return byeWeeks;
  } catch (error) {
    console.error('Error fetching NFL bye weeks:', error);
    return [];
  }
};

// getByeWeeks();